/**
 * Token provider — thin wrapper over AWS Amplify Auth v6 for the ID token.
 *
 * The MIPS Dashboard backend (v0.5.0) requires the **ID token** (not the
 * access token) in `Authorization: Bearer` because:
 *
 *   - The Pre-Token Generation Lambda V2 sets `aud="user"` only on the ID
 *     token; the backend verifier requires `aud="user"`.
 *   - The Lambda also injects `tenant_id` and `display_name` (from the
 *     user's `custom:tenant_id` attribute and best-effort name fields)
 *     only on the ID token.
 *
 * Sending the access token would fail verification with the `aud` claim
 * pointing to the Cognito app-client ID instead of "user". See the Tensaw
 * Auth Developer Guide §1.2.
 *
 * v3 library architecture: per the v3.1.0 release notes, the library no
 * longer owns auth/transport — the app supplies them. This file is that
 * supply for mips-dashboard-web. (Library v3.1.0 of `@tensaw/ui-runtime`
 * explicitly removed `tokenProvider`, `authenticatedFetch`, etc. from its
 * exports; they were moved to app ownership.)
 */
import { fetchAuthSession, signOut as amplifySignOut } from "aws-amplify/auth";

/**
 * Returns the current ID token if a Cognito session is valid; null if not
 * signed in or if Amplify can't deliver a session. Forces a fresh fetch
 * when `forceRefresh` is true (Amplify exchanges the refresh token for a
 * new ID token, which re-runs the Pre-Token Lambda).
 */
export async function getIdToken(opts?: { forceRefresh?: boolean }): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: opts?.forceRefresh ?? false });
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Signs the user out. By default does a local sign-out only (revokes the
 * refresh token + clears Amplify's local storage). Pass `{ global: true }`
 * to revoke all sessions across devices — used by admin-triggered logout
 * scenarios. The reload after signOut() returns the user to the
 * Authenticator's login form.
 */
export async function signOut(opts?: { global?: boolean; reload?: boolean }): Promise<void> {
  await amplifySignOut({ global: opts?.global ?? false });
  if (opts?.reload !== false) {
    window.location.reload();
  }
}
